import { useEffect } from 'react'
import { wealthboxSettingsDefaultValues, type WealthboxSettingsFormValues } from './models'
import { useWealthboxCalendarSync } from './hooks'
import { Controller, useForm } from 'react-hook-form'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { Link } from 'react-router-dom'
import { Button } from '../../../../../components/Button/Button'

export const WealthboxCalendarSyncSettings: React.FC = () => {
  const { checkpoint, isGetCheckpointFetching, isSaving, save } = useWealthboxCalendarSync()
  const form = useForm<WealthboxSettingsFormValues>({ defaultValues: wealthboxSettingsDefaultValues })

  useEffect(() => {
    if (isGetCheckpointFetching || !checkpoint) {
      return
    }
    form.setValue('inbound.calendar.enabled', checkpoint.data?.active || false)
  }, [isGetCheckpointFetching, checkpoint, form])

  const onSubmit = async (data: WealthboxSettingsFormValues) => {
    await save(data.inbound.calendar.enabled)
  }

  const handleCancel = () => {
    form.reset()
  }

  if (!isSaving && isGetCheckpointFetching) {
    return (
      <Container>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </Container>
    )
  }

  return (
    <Container className='pb-3'>
      <h6>Calendar</h6>
      <Row className='mb-1 mt-3'>
        <Col>
          <Stack direction='vertical' className='gap-4 pb-4 pb-md-0'>
            <div>
              Enable calendar synchronization if you use your CRM calendar to track meetings. Events will sync to Zocks, and your AI Assistant can join meetings
              automatically.
            </div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <Controller
              name='inbound.calendar.enabled'
              control={form.control}
              render={({ field, fieldState }) => (
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
                  <Form.Switch checked={field.value === true} onChange={field.onChange} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </ScopedBlock>
              )}
            />
          </Stack>
        </Col>
      </Row>
      <Row className='mb-1 mt-4'>
        <Col>
          <div className='alert alert-warning small'>
            Recurring meetings from Wealthbox cannot be synced due to API limitations. Individual meeting instances will still sync normally.
          </div>
        </Col>
      </Row>
      <hr></hr>
      <Row>
        <Col>
          <div className='paragraph-medium zocks-text-gray-600'>
            To ensure your AI Assistant joins all relevant meetings in your calendar, configure filtering and auto-join options on the{' '}
            <Link className='button-link-medium zocks-text-primary cursor-pointer' to='/account/profile-settings/meeting-assistant'>
              Event Filters
            </Link>{' '}
            page.
          </div>
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <div className='d-flex gap-3 justify-content-end mb-4'>
            <Button variant='secondary' onClick={handleCancel} disabled={isSaving}>
              Reset
            </Button>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Button variant='primary' onClick={form.handleSubmit(onSubmit)} loading={isSaving}>
                Save
              </Button>
            </ScopedBlock>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
